import { useState, useCallback, useRef, useEffect } from "react";
import { fetchTeamMessages, markTeamSpaceRead } from "../api/client";
import {
  TEAM_MESSAGES_PAGE_SIZE,
  mergeMessages,
  maxMessageId,
} from "../pages/Team/utils/teamMessagesState";
import { executeOptimisticTeamSend } from "../pages/Team/utils/teamOptimisticSend";

export const usePortalTeamMessages = (spaceId, company) => {
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextBefore, setNextBefore] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const loadingRef = useRef(false);
  const requestSeqRef = useRef(0);
  const messagesRef = useRef([]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const loadMessages = useCallback(
    async ({ before, append = false } = {}) => {
      if (!spaceId || !company || loadingRef.current) return;
      const requestId = ++requestSeqRef.current;
      loadingRef.current = true;
      setIsLoading(true);
      try {
        const result = await fetchTeamMessages(spaceId, {
          before,
          limit: TEAM_MESSAGES_PAGE_SIZE,
          company,
        });
        if (requestId !== requestSeqRef.current) return;

        const incoming = result.messages || [];

        if (append) {
          setMessages((prev) => mergeMessages(prev, incoming, "prepend"));
        } else {
          setMessages(mergeMessages([], incoming, "replace"));
        }

        setHasMore(Boolean(result.hasMore));
        setNextBefore(result.nextBefore);
      } catch {
        if (requestId !== requestSeqRef.current) return;
        if (!append) {
          setMessages([]);
          setHasMore(false);
          setNextBefore(null);
        }
      } finally {
        if (requestId === requestSeqRef.current) {
          loadingRef.current = false;
          setIsLoading(false);
        }
      }
    },
    [spaceId, company]
  );

  const syncLatest = useCallback(
    async ({ refreshRecent = false } = {}) => {
      if (!spaceId || !company) return;
      try {
        if (refreshRecent) {
          const result = await fetchTeamMessages(spaceId, {
            limit: TEAM_MESSAGES_PAGE_SIZE,
            company,
          });
          setMessages((prev) => mergeMessages(prev, result.messages || [], "merge"));
          return;
        }

        const after = maxMessageId(messagesRef.current);
        if (after) {
          const result = await fetchTeamMessages(spaceId, {
            after,
            limit: TEAM_MESSAGES_PAGE_SIZE,
            company,
          });
          if (result.messages?.length) {
            setMessages((prev) => mergeMessages(prev, result.messages, "merge"));
          }
          return;
        }

        const result = await fetchTeamMessages(spaceId, {
          limit: TEAM_MESSAGES_PAGE_SIZE,
          company,
        });
        setMessages(mergeMessages([], result.messages || [], "replace"));
        setHasMore(Boolean(result.hasMore));
        setNextBefore(result.nextBefore);
      } catch {
        /* ignore */
      }
    },
    [spaceId, company]
  );

  const pendingSendsRef = useRef(0);
  const [isSendingOptimistic, setIsSendingOptimistic] = useState(false);

  const sendMessage = useCallback(
    async (payload) => {
      if (!spaceId || !company) return;

      pendingSendsRef.current += 1;
      setIsSendingOptimistic(true);
      try {
        return await executeOptimisticTeamSend({
          spaceId,
          payload,
          setMessages,
          company,
          syncLatest,
        });
      } finally {
        pendingSendsRef.current = Math.max(0, pendingSendsRef.current - 1);
        if (pendingSendsRef.current === 0) {
          setIsSendingOptimistic(false);
        }
      }
    },
    [spaceId, company, syncLatest]
  );

  const markRead = useCallback(async () => {
    if (!spaceId || !company) return;
    try {
      await markTeamSpaceRead(spaceId, company);
    } catch {
      /* ignore */
    }
  }, [spaceId, company]);

  const reset = useCallback(() => {
    requestSeqRef.current += 1;
    setMessages([]);
    setHasMore(false);
    setNextBefore(null);
  }, []);

  return {
    messages,
    hasMore,
    isLoading,
    loadMessages,
    loadMore: () => {
      if (hasMore && nextBefore) loadMessages({ before: nextBefore, append: true });
    },
    syncLatest,
    syncRecentMessages: () => syncLatest({ refreshRecent: true }),
    sendMessage,
    isSending: isSendingOptimistic,
    markRead,
    reset,
  };
};
